import { store, storeActions } from '~/utility/BinSentry-ui-utility';
import { css, html, LitEntity, renderIf, SirenActionMixin } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../props/bin-name';
import '../props/bin-owner';
import './bin-list-item';
import '../../common/bn-load-more-button';
import '../../common/bn-flash';
import './bin-levels-admin-list';
import { StoreMixin } from '../../utility/store-mixin';

class BinLevelsAdmin extends StoreMixin(SirenActionMixin(LitEntity)) {
  static get properties() {
    return {
      binsHref: { type: String, attribute: false },
      nextHref: { type: String, attribute: false },
      printMode: { type: Boolean, attribute: 'print-mode' },
      isFetching: { type: Boolean, attribute: false },
      showGraphToolbars: { type: Boolean, attribute: false },
      showRaw: { type: Boolean, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: flex;
        flex-direction: column;
      }
      p {
        text-align: center;
        color: var(--gray-80);
      }
      .list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      }
      h3 {
        text-align: center;
      }
      .controls {
        display: flex;
        justify-content: space-around;
        align-items: center;
        margin: var(--root-margin);
      }
      @media print {
        .controls, h3 {
          display: none;
        }
      }
    `];
  }

  render() {
    if (this.loading) {
      return html`<bn-loading></bn-loading>`;
    }

    const firstPageUrl = this._getFirstPageUrl(this.href);
    return html`
      <div class="controls">
        <a href="${firstPageUrl}" @click="${() => this._getBinsHref()}">
          First Page
        </a>

        <button
          @click="${this._onTogglePrintMode}"
          class="button button--contained"
        >
          ${this.printMode ? 'ⓧ Exit Print Mode' : '🖨 Enter Print Mode'}
        </button>

        <button
          @click="${this._onToggleGraphToolbars}"
          class="button button--contained"
        >
          ${this.showGraphToolbars ? '☑' : '☐'} Graph Toolbars
        </button>

        <button
          @click="${this._onToggleRaw}"
          class="button button--contained"
        >
          ${this.showRaw ? '☑' : '☐'} Show Raw
        </button>

        <button
          @click="${this._onOpenAllBins}"
          class="button button--contained"
        >
          Open All Bins In Tabs
        </button>

        ${renderIf(this.nextHref, () => html`
          <a href="${`${firstPageUrl}&binsHref=${encodeURIComponent(this.nextHref)}`}">
            Next Page
          </a>
        `)}

        ${renderIf(this.entity && !this.nextHref, () => html`
          <span>No More Pages</span>
        `)}

        <label>
          New Organization Id
          <input type="text" @change="${this._onOrgIdEntered}" />
        </label>
      </div>

      <bn-flash id="status"></bn-flash>

      ${renderIf(!this.isFetching && (!this.entity || !this.binsHref), () => html`
        <h3>Organization Not Found</h3>
      `)}

      ${renderIf(this.binsHref, () => html`
        <h3>${this.entity.properties.name}</h3>
        <bin-levels-admin-list
          .href="${this.binsHref}"
          .orgId="${this.href}"
          ?show-raw="${this.showRaw}"
          ?show-graph-toolbars="${this.showGraphToolbars}"
          ?print-mode="${this.printMode}"
          @update="${this._onBinLevels}"
        ></bin-levels-admin-list>
      `)}
    `;
  }

  _getFirstPageUrl(orgId) {
    return `/bin-levels-admin?printMode=true&orgId=${encodeURIComponent(orgId)}`;
  }

  _clearStatus() {
    const flash = this.shadowRoot.querySelector('#status');
    if (!flash) {
      return;
    }

    flash.clear();
  }

  async _entityChanged(entity) {
    super._entityChanged(entity);
    if (!this.entity || this.binsHref) {
      return;
    }
    this._getBinsHref();
  }

  async _getBinsHref() {
    this.isFetching = true;
    this._clearStatus();
    try {
      const baseBinsHref = this.getHref('https://api.binsentry.com/rel/v2/bins');
      const binsEntity = await this.fetchSiren(baseBinsHref);
      const action = binsEntity.getActionByName('limit');
      const fields = this.getActionFields(action);
      fields.set('limit', 50);
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('binsHref', this.parseAction(action, fields).url.href);
      store.dispatch(storeActions.navigate(`${window.location.pathname}?${searchParams.toString()}`));
    } catch (e) {
      const flash = this.shadowRoot.querySelector('#status');
      flash.critical(html`Failed to load`);
    }

    this.isFetching = false;
  }

  _onBinLevels(e) {
    const { entity } = e.detail;
    this.binsEntity = entity;
    if (!entity) {
      return;
    }

    this.nextHref = this.getHref('next', entity);
  }

  _stateChanged({ query }) {
    if (query) {
      this.binsHref = query.binsHref;

      if (!query.printMode && !this.navigating) {
        const currentHref = window.location.href;
        const hasQueryParams = currentHref.includes('?');
        this.navigating = true;
        store.dispatch(storeActions.navigate(`${window.location.href}${hasQueryParams ? '&' : '?'}printMode=true`));
      }
    }
  }

  _onTogglePrintMode() {
    this.printMode = !this.printMode;
  }

  _onToggleGraphToolbars() {
    this.showGraphToolbars = !this.showGraphToolbars;
  }

  _onToggleRaw() {
    this.showRaw = !this.showRaw;
  }

  _onOpenAllBins() {
    const bins = this.binsEntity.getSubEntitiesByRel('https://api.binsentry.com/rel/bin');
    for (const bin of bins) {
      window.open(`/bins/show/overview?binId=${encodeURIComponent(bin.getLinkByRel('self').href)}`);
    }
  }

  _onOrgIdEntered(e) {
    store.dispatch(storeActions.navigate(this._getFirstPageUrl(e.target.value)));
  }
}

window.customElements.define('bin-levels-admin', BinLevelsAdmin);
